<script lang="ts" setup>
import { ref, computed, watch, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';

import store from '@/store';
import { useFilterStore, useUiStore } from '@/stores';
import ConnectFlowAudioService from '@/services/api/ConnectFlowAudioService';
import { toast, toastServiceError } from '@/services/core/notification';

import {
    FilterItem,
    SortMenuItem,
    ConnectFlowAudioModel,
    ConnectFlowAudioFilter,
    ConnectFlowAudioServiceGetAllParams,
    Permission,
    ConnectFlowAudioActions,
    ConnectFlowAudioGetters,
} from '@/types';

import {
    ConnectFlowAudioListItem,
    ConnectFlowAudioListSort,
    ConnectFlowAudioListFilters,
} from '@/views/pages/connect-flow-audio/components';

import { UserGroupIcon } from '@heroicons/vue/solid';

const route = useRoute();
const router = useRouter();

const filterStore = useFilterStore();
const uiStore = useUiStore();

const connectFlowAudios = ref<ConnectFlowAudioModel[]>([]);
const count = ref(0);
const perPage = 10;

const search = computed({
    get: () => store.getters[ConnectFlowAudioGetters.SEARCH],
    set: (value: string) => store.dispatch(ConnectFlowAudioActions.SET_SEARCH, value),
});

const sortMenuSelected = computed<SortMenuItem[]>({
    get: () => store.getters[ConnectFlowAudioGetters.SORT],
    set: (value: SortMenuItem[]) => store.dispatch(ConnectFlowAudioActions.SET_SORT, value),
});

const levelFilter = computed(() => filterStore.level);

const currentPage = computed({
    get: () => store.getters[ConnectFlowAudioGetters.PAGE],
    set: (value: number) => store.dispatch(ConnectFlowAudioActions.SET_PAGE, value),
});

const filter: ConnectFlowAudioFilter = store.getters[ConnectFlowAudioGetters.FILTER];

const activeFilters = computed<FilterItem[]>(
    () => store.getters[ConnectFlowAudioGetters.ACTIVE_FILTERS],
);

const orderBy = computed(() => sortMenuSelected.value.map((s) => s.direction + s.key).join(','));

const setLoading = (loading: boolean) => uiStore.setIsLoading(loading);

const getParams = (): ConnectFlowAudioServiceGetAllParams => ({
    page: currentPage.value,
    per_page: perPage,
    order_by: orderBy.value,
    is_active: filter.isActive !== null ? (filter.isActive.value as boolean) : undefined,
    search: search.value,
});

const fetchConnectFlowAudios = async () => {
    await setLoading(true);
    try {
        const { data, headers } = await ConnectFlowAudioService.getAll<ConnectFlowAudioModel[]>({
            params: getParams(),
        });
        connectFlowAudios.value = data;
        count.value = parseInt(headers['x-pagination-count']);
    } catch (error) {
        toastServiceError(error);
    } finally {
        await setLoading(false);
    }
};

const setIsActive = async (item: ConnectFlowAudioModel, isActive: boolean) => {
    await setLoading(true);
    try {
        const { data } = await (
            isActive
                ? (id: number) => ConnectFlowAudioService.activate(id)
                : (id: number) => ConnectFlowAudioService.inactivate(id)
        )(item.id);
        toast.success(data.message);
        await fetchConnectFlowAudios();
    } catch (error) {
        toastServiceError(error);
    } finally {
        await setLoading(false);
    }
};

const handleActivateClick = (item: ConnectFlowAudioModel) => setIsActive(item, true);
const handleInactivateClick = (item: ConnectFlowAudioModel) => setIsActive(item, false);

const handleRemoveSortClick = (item: SortMenuItem) => {
    const index = sortMenuSelected.value.indexOf(item);
    if (index > -1) sortMenuSelected.value.splice(index, 1);
};

const handleRemoveFilterClick = (item: FilterItem) =>
    store.dispatch(ConnectFlowAudioActions.CLEAR_FILTER, item);

watch([currentPage], () => {
    fetchConnectFlowAudios();
});

watch([levelFilter, search, filter, orderBy], () => {
    if (currentPage.value === 1) fetchConnectFlowAudios();
    else currentPage.value = 1;
});

if (route.query.clearStateOnSetup) {
    search.value = '';
    sortMenuSelected.value = [];
    currentPage.value = 1;
    router.replace({ query: {} });
}

onMounted(fetchConnectFlowAudios);
</script>

<template>
    <div class="space-y-4 p-4 sm:p-6 lg:p-8">
        <UiPageHeader>
            <template #info>
                <h1 class="flex items-center text-xl font-semibold text-gray-800">
                    <UserGroupIcon class="mr-1 h-6 w-6 text-gray-500" aria-hidden="true" />
                    {{ $t('core.entity.ConnectFlowAudios') }}
                </h1>
            </template>
            <template #actions>
                <router-link
                    v-if="$can(Permission.CREATE_CONNECT_FLOW_AUDIO)"
                    :to="{ name: 'CreateConnectFlowAudio' }"
                >
                    <UiButton variant="primary" text-variant="white">
                        {{ $t('core.actions.Add') }}
                    </UiButton>
                </router-link>
            </template>
        </UiPageHeader>

        <div class="space-y-2">
            <UiPanel class="flex px-4 py-2" no-padding>
                <UiListSearch
                    v-model="search"
                    :placeholder="$t('connectFlowAudio.filters.search')"
                    class="flex-1"
                />
                <div class="flex items-center space-x-4">
                    <ConnectFlowAudioListSort v-model="sortMenuSelected" />
                    <ConnectFlowAudioListFilters />
                </div>
            </UiPanel>

            <UiPanel v-if="sortMenuSelected.length || activeFilters.length" class="space-x-2">
                <UiSortMenuBadge
                    v-for="selected in sortMenuSelected"
                    :key="selected.key"
                    :item="selected"
                    @remove="handleRemoveSortClick(selected)"
                />
                <UiBadge
                    v-for="item in activeFilters"
                    :key="`activeFilter${item.field}`"
                    removable
                    @remove="handleRemoveFilterClick(item)"
                >
                    {{ $t(`connectFlowAudio.filters.${item.field}.label`) }}: {{ item.label }}
                </UiBadge>
            </UiPanel>

            <div v-if="connectFlowAudios.length" class="space-y-2">
                <ConnectFlowAudioListItem
                    v-for="connectFlowAudio in connectFlowAudios"
                    :key="connectFlowAudio.id"
                    :model-value="connectFlowAudio"
                    @activate="handleActivateClick"
                    @inactivate="handleInactivateClick"
                />
            </div>
            <UiPanel v-else class="flex items-center justify-center">
                {{ $t('core.messages.noRecordsFound') }}
            </UiPanel>

            <UiPagination
                v-if="connectFlowAudios.length"
                v-model="currentPage"
                :count="count"
                :perPage="perPage"
            />
        </div>
    </div>
</template>
